import type { MenuProps } from '@mui/material';
import { Button, FormControlLabel, InputLabel } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import type { SelectProps as originalSelectProps } from '@mui/material/Select';
import Select from '@mui/material/Select';
import { isEmpty, isNull, isUndefined } from 'lodash';
import type { FunctionComponent, SVGProps } from 'react';
import React, { useState } from 'react';

import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow.svg';
import CheckboxField from './CheckboxField';
import TextField from './TextField';

export interface ISelectOptions {
  value: any; // eslint-disable-line
  label: string | React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps extends Omit<originalSelectProps, 'IconComponent'> {
  options: ISelectOptions[];
  customIcon?: React.ElementType;
  menuProps?: Partial<MenuProps>;
  allowSearch?: boolean;
  isReadonly?: boolean;
  wrapperStyle?: any; // eslint-disable-line
  clearAll?(): void;
  showMoreOptions?: ISelectOptions[];
  selectedClassName?: string;
  wrapperClassName?: string;
  IconComponent?: FunctionComponent<SVGProps<SVGSVGElement>>;
}

export default React.forwardRef(
  (
    {
      name,
      value,
      label,
      options,
      isReadonly,
      wrapperStyle,
      clearAll,
      allowSearch,
      customIcon,
      menuProps,
      showMoreOptions,
      selectedClassName,
      wrapperClassName,
      defaultValue,
      ...props
    }: SelectProps,
    ref,
  ) => {
    const [checked, setChecked] = useState(false);
    const [search, setSearch] = useState<string>('');
    const [showMore, setShowMore] = useState<boolean>(false);
    const [selectAutoFocus, setSelectAutoFocus] = useState<boolean>(true);

    return (
      <div
        className={wrapperClassName}
        style={{ display: 'flex', flexDirection: 'column', ...wrapperStyle }}
      >
        {label && <InputLabel id={`label-for-${name}`}>{label}</InputLabel>}

        {isReadonly ? (
          <div>{options.find((x) => x.value === value)?.label}</div>
        ) : (
          <Select
            MenuProps={
              menuProps
                ? {
                    ...menuProps,
                    autoFocus: selectAutoFocus,
                  }
                : { autoFocus: false }
            }
            value={value}
            defaultValue={
              !isUndefined(defaultValue) && !isNull(defaultValue)
                ? defaultValue
                : props.multiple
                  ? []
                  : ''
            }
            inputRef={ref}
            name={name}
            IconComponent={customIcon ? customIcon : ArrowIcon}
            {...props}
            label={label}
            {...(allowSearch && {
              onClose: (e) => {
                setSearch('');
                props?.onClose && props.onClose(e);
              },
            })}
          >
            {allowSearch ? (
              <TextField
                placeholder="Search"
                autoFocus
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (selectAutoFocus) {
                    setSelectAutoFocus(!selectAutoFocus);
                  }
                }}
                type="search"
                onKeyDown={(e) => {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }}
                onBlur={() => setSelectAutoFocus(true)}
                wrapperClassName="p-2 sticky z-10 top-0 bg-white"
                size="small"
                variant="outlined"
              />
            ) : null}
            {clearAll && (
              <div className="flex flex-row justify-start pl-4  pb-2 cursor-pointer">
                <FormControlLabel
                  control={
                    <CheckboxField
                      checked={checked}
                      onChange={(event) => {
                        setChecked(event.target.checked);
                        if (event.target.checked) {
                          clearAll();
                          setChecked(false);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label="Clear All"
                />
              </div>
            )}
            {[...options, ...(showMore && showMoreOptions ? showMoreOptions : [])]
              ?.filter(
                (option) =>
                  (typeof option?.label === 'string' &&
                    option.label?.toLowerCase().includes(search?.toLowerCase())) ||
                  (React.isValidElement<{ children?: string[] }>(option?.label) &&
                    option.label?.props?.children
                      ?.join(' - ')
                      ?.toLowerCase()
                      .includes(search?.toLowerCase())) ||
                  search === '',
              )
              ?.map((x, index) => (
                <MenuItem
                  key={`${JSON.stringify(x.value)}-${index}`}
                  value={x.value}
                  classes={{
                    selected: selectedClassName ?? '',
                  }}
                  disabled={x.disabled}
                >
                  {x.label}
                </MenuItem>
              ))}
            {!isEmpty(showMoreOptions) ? (
              <Button
                variant="text"
                color="primary"
                classes={{
                  root: 'text-center text-base  m-auto flex',
                }}
                onClick={() => {
                  setShowMore(!showMore);
                }}
              >
                Show {showMore ? 'less' : 'more'}
              </Button>
            ) : null}
          </Select>
        )}
      </div>
    );
  },
);
